import { HugeiconsIcon } from "@hugeicons/react";
import { Alert01Icon } from "@hugeicons/core-free-icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearch } from "@tanstack/react-router";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import type { UIMessage } from "ai";
import { useE2EE } from "@/providers/E2EEProvider";
import { useModelStore } from "@/stores/modelStore";
import {
  useChat,
  useUpdateChat,
  useDeleteChat,
  useCreateChat,
} from "@/hooks/queries/useChats";
import { trpc } from "@/lib/trpc";
import {
  getChatKey,
  decryptChatTitle,
  decryptChatContent,
  encryptChatContent,
  encryptChatTitle,
  createEncryptedChat,
} from "@onera/crypto";
import type { ChatMessage, MessageContent, ChatHistory } from "@onera/types";
import {
  toUIMessage,
  toChatMessage,
  areMessagesEqual,
} from "@/lib/chat/messageUtils";
import { Messages } from "@/components/chat/Messages";
import {
  createMessagesList,
  createBranchFromEdit,
  switchToBranch,
  deleteMessage,
} from "@/lib/messageTree";
import {
  MessageInput,
  type MessageInputOptions,
} from "@/components/chat/MessageInput";
import { ChatNavbar } from "@/components/chat/ChatNavbar";
import { ModelSelector } from "@/components/chat/ModelSelector";
import { useDirectChat } from "@/hooks/useDirectChat";
import {
  generateFollowUps,
  generateChatTitle,
  getProviderFromModelId,
  supportsNativeSearch,
} from "@/lib/ai";
import {
  executeSearch,
  formatSearchResultsForContext,
  type SearchResult,
} from "@/lib/search";
import type { Source } from "@/components/chat/Sources";
import { useToolsStore, type NativeSearchProvider } from "@/stores/toolsStore";
import { useChatRuntimeStore } from "@/stores/chatRuntimeStore";
import { getTextContent, pendingTriggerSignature } from "@/lib/chat/pipeline";
import { Spinner } from "@/components/ui/spinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { normalizeAppError } from "@/lib/errors/app-error";
import { cn } from "@/lib/utils";
import { analytics } from "@/lib/analytics";

interface DecryptedChat {
  id: string;
  title: string;
  messages: ChatMessage[];
  history: ChatHistory;
  createdAt: number;
  updatedAt: number;
  encryptedChatKey?: string;
  chatKeyNonce?: string;
  decryptionError?: string;
}

export function ChatPage() {
  const { chatId } = useParams({ strict: false });
  const { pending, messageId: highlightMessageId } = useSearch({ strict: false }) as { pending?: boolean; messageId?: string };
  const navigate = useNavigate();
  const { isUnlocked } = useE2EE();
  const { selectedModelId, setSelectedModel } = useModelStore();
  const { getNativeSearchSettings } = useToolsStore();
  const setChatRuntimeStatus = useChatRuntimeStore((state) => state.setStatus);
  const clearChatRuntime = useChatRuntimeStore((state) => state.clearChat);

  // Track if we've triggered the pending response (prevents duplicate calls)
  const pendingTriggeredRef = useRef(false);
  const lastPendingSignatureRef = useRef<string | null>(null);
  // Track if we should skip message sync (for pending chats)
  // This is managed by effects, not the initializer, to handle navigation between chats
  const skipSyncRef = useRef(false);
  // Track the last processed chatId to detect chat changes
  const lastChatIdRef = useRef<string | undefined>(undefined);

  // Track if stop was called internally (by user clicking stop button)
  const isInternalStopRef = useRef(false);

  // Follow-up suggestions state
  const [followUps, setFollowUps] = useState<string[]>([]);
  const [isGeneratingFollowUps, setIsGeneratingFollowUps] = useState(false);
  const [decryptAttempt, setDecryptAttempt] = useState(0);
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);

  // Search sources state - stores results from the current/last search
  const [searchSources, setSearchSources] = useState<Source[]>([]);

  // Determine native search settings based on current provider
  const nativeSearch = useMemo(() => {
    if (!selectedModelId) return undefined;

    const provider = getProviderFromModelId(selectedModelId);
    if (!provider || !supportsNativeSearch(provider)) return undefined;

    const settings = getNativeSearchSettings(provider as NativeSearchProvider);
    if (!settings?.enabled) return undefined;

    return {
      enabled: true,
      settings,
    };
  }, [selectedModelId, getNativeSearchSettings]);

  // Fetch chat using Convex
  const rawChat = useChat(chatId || "");
  const updateChatMutation = useUpdateChat();
  const deleteChatMutation = useDeleteChat();
  const createChatMutation = useCreateChat();
  const utils = trpc.useUtils();

  // Decrypt chat data
  const chat = useMemo((): DecryptedChat | null => {
    // Lazy creation: if no chatId, return a virtual empty chat
    if (!chatId) {
      return {
        id: "new",
        title: "New Chat",
        messages: [],
        history: { currentId: null, messages: {} },
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
    }

    if (!rawChat) return null;
    void decryptAttempt;

    if (isUnlocked && rawChat.encryptedChatKey && rawChat.chatKeyNonce) {
      try {
        getChatKey(rawChat.id, rawChat.encryptedChatKey, rawChat.chatKeyNonce);

        const title =
          rawChat.encryptedTitle && rawChat.titleNonce
            ? decryptChatTitle(
              rawChat.id,
              rawChat.encryptedChatKey,
              rawChat.chatKeyNonce,
              rawChat.encryptedTitle,
              rawChat.titleNonce,
            )
            : "Untitled";

        const history: ChatHistory =
          rawChat.encryptedChat && rawChat.chatNonce
            ? (decryptChatContent(
              rawChat.id,
              rawChat.encryptedChatKey,
              rawChat.chatKeyNonce,
              rawChat.encryptedChat,
              rawChat.chatNonce,
            ) as unknown as ChatHistory)
            : { currentId: null, messages: {} };

        // Get linear messages from history for display
        const messages = createMessagesList(history);

        return {
          id: rawChat.id,
          title,
          messages,
          history,
          createdAt: rawChat.createdAt,
          updatedAt: rawChat.updatedAt,
          encryptedChatKey: rawChat.encryptedChatKey,
          chatKeyNonce: rawChat.chatKeyNonce,
        };
      } catch {
        const emptyHistory: ChatHistory = { currentId: null, messages: {} };
        return {
          id: rawChat.id,
          title: "Encrypted",
          messages: [],
          history: emptyHistory,
          createdAt: rawChat.createdAt,
          updatedAt: rawChat.updatedAt,
          encryptedChatKey: rawChat.encryptedChatKey,
          chatKeyNonce: rawChat.chatKeyNonce,
          decryptionError:
            "Unable to decrypt this chat in the current session. Re-unlock encryption or retry.",
        };
      }
    }

    const emptyHistory: ChatHistory = { currentId: null, messages: {} };
    return {
      id: rawChat.id,
      title: "Encrypted",
      messages: [],
      history: emptyHistory,
      createdAt: rawChat.createdAt,
      updatedAt: rawChat.updatedAt,
      encryptedChatKey: rawChat.encryptedChatKey,
      chatKeyNonce: rawChat.chatKeyNonce,
    };
  }, [rawChat, isUnlocked, decryptAttempt, chatId]);

  const isLoading = !!chatId && rawChat === undefined;

  // Ref to track pending user message for persistence (ref needed for async callbacks)
  const pendingUserMessageRef = useRef<ChatMessage | null>(null);
  // Ref to track current history for persistence (required for async callbacks)
  const currentHistoryRef = useRef<ChatHistory>({
    currentId: null,
    messages: {},
  });
  // Ref to track previous display messages for stable references (React memo optimization)
  const prevDisplayMessagesRef = useRef<ChatMessage[]>([]);
  // Ref to track current display messages for callbacks (avoids recreating callbacks during streaming)
  const displayMessagesRef = useRef<ChatMessage[]>([]);
  // Refs for encryption keys - stable during streaming to avoid callback recreation
  const encryptionKeysRef = useRef<{
    encryptedChatKey?: string;
    chatKeyNonce?: string;
  }>({});
  // Refs for mutation functions to avoid callback recreation
  const deleteChatMutationRef = useRef(deleteChatMutation);
  const updateChatMutationRef = useRef(updateChatMutation);

  // Local history state for immediate UI updates (server query may lag behind)
  const [localHistory, setLocalHistory] = useState<ChatHistory>({
    currentId: null,
    messages: {},
  });

  // Keep currentHistoryRef and localHistory in sync with chat
  useEffect(() => {
    if (chat?.history) {
      currentHistoryRef.current = chat.history;
      setLocalHistory(chat.history);
    }
  }, [chat?.history]);

  // Keep encryption keys ref in sync - these are stable during streaming
  useEffect(() => {
    if (chat?.encryptedChatKey && chat?.chatKeyNonce) {
      encryptionKeysRef.current = {
        encryptedChatKey: chat.encryptedChatKey,
        chatKeyNonce: chat.chatKeyNonce,
      };
    }
  }, [chat?.encryptedChatKey, chat?.chatKeyNonce]);

  // Keep mutation refs in sync (no dependency tracking needed - always update)
  useEffect(() => {
    deleteChatMutationRef.current = deleteChatMutation;
    updateChatMutationRef.current = updateChatMutation;
  });

  // Handle message completion - persist user + assistant messages
  const handleFinish = useCallback(
    async (message: UIMessage) => {
      const currentChatId = lastChatIdRef.current || chatId;
      if (!currentChatId || !chat?.encryptedChatKey || !chat?.chatKeyNonce) return;

      // Capture encryption keys for use in async callbacks
      const encryptedChatKey = chat.encryptedChatKey;
      const chatKeyNonce = chat.chatKeyNonce;

      // Build final messages: stored + pending user + assistant
      const pendingUserMessage = pendingUserMessageRef.current;
      const assistantMessage = toChatMessage(
        message,
        selectedModelId || undefined,
      );

      // Get current history or initialize empty (deep copy to avoid mutations)
      const history: ChatHistory = {
        currentId: currentHistoryRef.current.currentId,
        messages: { ...currentHistoryRef.current.messages },
      };

      // Find the current leaf message (parent for new messages)
      let parentId: string | null = history.currentId;

      // Add pending user message to history
      if (pendingUserMessage) {
        // Check if this user message already exists (e.g., pending from home.tsx navigation)
        const existingUserMessage = history.messages[pendingUserMessage.id];

        if (existingUserMessage) {
          // User message already in history - just use it as parent for assistant
          parentId = pendingUserMessage.id;
        } else {
          // New user message - add to history with tree structure
          const userMsgWithTree: ChatMessage = {
            ...pendingUserMessage,
            parentId,
            childrenIds: [],
          };

          // Update parent's childrenIds
          if (parentId && history.messages[parentId]) {
            history.messages[parentId] = {
              ...history.messages[parentId],
              childrenIds: [
                ...(history.messages[parentId].childrenIds || []),
                pendingUserMessage.id,
              ],
            };
          }

          history.messages[pendingUserMessage.id] = userMsgWithTree;
          parentId = pendingUserMessage.id;
        }

        pendingUserMessageRef.current = null; // Clear pending
        setPendingMessageId(null);
      }

      // Add assistant message to history
      const assistantMsgWithTree: ChatMessage = {
        ...assistantMessage,
        parentId,
        childrenIds: [],
      };

      // Update parent's childrenIds if it exists
      if (parentId && history.messages[parentId]) {
        history.messages[parentId] = {
          ...history.messages[parentId],
          childrenIds: [
            ...(history.messages[parentId].childrenIds || []),
            assistantMessage.id,
          ],
        };
      }

      history.messages[assistantMessage.id] = assistantMsgWithTree;
      history.currentId = assistantMessage.id;

      // Update history ref and local state for immediate UI updates
      currentHistoryRef.current = history;
      setLocalHistory(history);

      // Encrypt and save to server
      try {
        const encrypted = encryptChatContent(
          currentChatId,
          encryptedChatKey,
          chatKeyNonce,
          history as unknown as Record<string, unknown>,
        );

        await updateChatMutation.mutateAsync({
          id: currentChatId,
          data: {
            encryptedChat: encrypted.encryptedChat,
            chatNonce: encrypted.chatNonce,
          },
        });

        // If the generation was stopped by the user, don't generate follow-ups
        if (isInternalStopRef.current) {
          isInternalStopRef.current = false;
          return;
        }

        // Generate follow-up suggestions asynchronously and persist them
        if (selectedModelId) {
          setIsGeneratingFollowUps(true);
          // Derive messages from history for follow-up generation
          const messagesForFollowUps = createMessagesList(
            currentHistoryRef.current,
          );
          generateFollowUps(messagesForFollowUps, selectedModelId, 3)
            .then(async (suggestions) => {
              setFollowUps(suggestions);

              // Persist follow-ups to the assistant message
              if (
                suggestions.length > 0 &&
                currentHistoryRef.current.messages[assistantMessage.id]
              ) {
                currentHistoryRef.current.messages[assistantMessage.id] = {
                  ...currentHistoryRef.current.messages[assistantMessage.id],
                  followUps: suggestions,
                };

                // Re-encrypt and save with follow-ups
                try {
                  const encryptedWithFollowUps = encryptChatContent(
                    currentChatId,
                    encryptedChatKey,
                    chatKeyNonce,
                    currentHistoryRef.current as unknown as Record<
                      string,
                      unknown
                    >,
                  );

                  await updateChatMutation.mutateAsync({
                    id: currentChatId,
                    data: {
                      encryptedChat: encryptedWithFollowUps.encryptedChat,
                      chatNonce: encryptedWithFollowUps.chatNonce,
                    },
                  });
                } catch (followUpSaveError) {
                  console.warn(
                    "Failed to persist follow-ups:",
                    followUpSaveError,
                  );
                }
              }
            })
            .catch((err) => {
              console.warn("Failed to generate follow-ups:", err);
            })
            .finally(() => {
              setIsGeneratingFollowUps(false);
            });

          // Generate AI title for new chats if the title is still default
          const isDefaultTitle = chat.title === "New Chat" || chat.title === "Untitled" || !chat.title;
          const messageCount = Object.keys(currentHistoryRef.current.messages).length;

          if (messageCount >= 2 && isDefaultTitle) {
            generateChatTitle(messagesForFollowUps, selectedModelId)
              .then(async (generatedTitle) => {
                if (generatedTitle && generatedTitle !== chat.title) {
                  try {
                    const encryptedTitle = encryptChatTitle(
                      currentChatId,
                      encryptedChatKey,
                      chatKeyNonce,
                      generatedTitle,
                    );

                    await updateChatMutation.mutateAsync({
                      id: currentChatId,
                      data: {
                        encryptedTitle: encryptedTitle.encryptedTitle,
                        titleNonce: encryptedTitle.titleNonce,
                      },
                    });
                  } catch (titleSaveError) {
                    console.warn(
                      "Failed to save generated title:",
                      titleSaveError,
                    );
                  }
                }
              })
              .catch((err) => {
                console.warn("Failed to generate title:", err);
              });
          }
        }
      } catch (saveError) {
        console.error("Failed to save chat:", saveError);
        toast.error("Message sent but failed to save");
      }

      // If this was a new chat, now navigate to update the URL
      if (!chatId && currentChatId) {
        // Force refetch so the new component mounts with the generated messages
        await utils.chats.get.invalidate({ chatId: currentChatId });
        navigate({
          to: "/app/c/$chatId",
          params: { chatId: currentChatId },
          search: { pending: false },
          replace: true,
        });
      }
    },
    [
      chatId,
      chat?.encryptedChatKey,
      chat?.chatKeyNonce,
      selectedModelId,
      updateChatMutation,
      navigate,
      utils,
    ],
  );

  // Use the direct chat hook
  const {
    messages: aiMessages,
    setMessages,
    sendMessage,
    status,
    stop: stopRaw,
    isReady,
    isLoadingCredentials,
  } = useDirectChat({
    chatId: chatId || "new",
    onFinish: handleFinish,
    onError: (error) => {
      if (error.name !== "AbortError") {
        const normalized = normalizeAppError(error, "Failed to get response", {
          stage: "chat_stream",
          chatId,
        });
        toast.error(normalized?.userMessage ?? (error.message || "Failed to get response"));
      }
    },
    nativeSearch,
  });

  // Wrap stop to track internal stop state
  const stop = useCallback(() => {
    isInternalStopRef.current = true;
    analytics.chat.messageStreamStopped();
    stopRaw();
  }, [stopRaw]);

  // Sync decrypted messages to AI SDK state when chat loads
  // Skip syncing if this is a pending chat - we'll let the AI SDK handle messages naturally
  useEffect(() => {
    // Detect if this is a new chat navigation
    const isNewChat = lastChatIdRef.current !== chatId;
    if (isNewChat) {
      lastChatIdRef.current = chatId;
      // Reset refs for the new chat
      pendingTriggeredRef.current = false;
      lastPendingSignatureRef.current = null;
      // Skip sync only if this new chat has pending=true
      skipSyncRef.current = pending === true;
    }

    // Skip if this is a pending chat - the pending handler will trigger sendMessage
    if (skipSyncRef.current) return;

    if (chat?.messages && chat.messages.length > 0) {
      const uiMessages = chat.messages.map(toUIMessage);
      setMessages(uiMessages);

      // Load persisted follow-ups from the last assistant message
      const lastMessage = chat.messages[chat.messages.length - 1];
      if (
        lastMessage?.role === "assistant" &&
        lastMessage.followUps &&
        lastMessage.followUps.length > 0
      ) {
        // Use persisted follow-ups
        setFollowUps(lastMessage.followUps);
      } else {
        // Clear any stale follow-ups if the last message doesn't have them
        setFollowUps([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.id, chatId, pending]); // Depend on chatId and pending to handle navigation

  // Handle pending response from home.tsx navigation
  // When navigated with ?pending=true, trigger AI response for the user message
  useEffect(() => {
    const triggerPendingResponse = async () => {
      // Only trigger once per chat
      if (
        !pending ||
        pendingTriggeredRef.current ||
        !chat ||
        !isReady ||
        !selectedModelId
      ) {
        return;
      }

      // Mark as triggered to prevent duplicate calls
      pendingTriggeredRef.current = true;

      // Clear the pending param from URL without triggering navigation
      navigate({
        to: "/app/c/$chatId",
        params: { chatId: chatId! },
        search: { pending: false },
        replace: true,
      });

      // Get the user message from the chat
      const messages = chat.messages;
      if (messages.length === 0) return;

      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role !== "user") return;
      const pendingSignature = pendingTriggerSignature(chatId!, lastMessage.id);
      if (lastPendingSignatureRef.current === pendingSignature) {
        return;
      }
      lastPendingSignatureRef.current = pendingSignature;

      // Extract user message content
      const userContent = getTextContent(lastMessage);

      if (!userContent) return;

      // Set the pending user message so displayMessages uses the stored message
      // This ensures consistent message IDs between stored history and display
      pendingUserMessageRef.current = lastMessage;

      try {
        // Reconstruct multimodal payload if necessary
        let contentInput: any = userContent;
        if (Array.isArray(lastMessage.content)) {
          const parts: any[] = [];
          for (const c of lastMessage.content as any[]) {
            if (c.type === 'text') {
              parts.push({ type: 'text', text: c.text });
            } else if (c.type === 'image_url') {
              parts.push({ type: 'file', url: c.image_url?.url, mediaType: 'image/png' });
            } else if (c.type === 'document_url') {
              if (c.document_url?.extractedText) {
                parts.push({ type: 'text', text: `[Document: ${c.document_url.fileName}]\n${c.document_url.extractedText}\n\n` });
              }
            }
          }
          if (parts.length > 0) contentInput = { parts };
        }

        // Send the message to trigger AI response, PRESERVING the user message ID
        await sendMessage(contentInput, { id: lastMessage.id });
      } catch (err) {
        lastPendingSignatureRef.current = null;
        if ((err as Error).name !== "AbortError") {
          toast.error(
            err instanceof Error ? err.message : "Failed to get response",
          );
        }
      }
    };

    triggerPendingResponse();
  }, [pending, chat, isReady, selectedModelId, chatId, navigate, sendMessage]);

  // Derive streaming state from AI SDK status
  const isStreaming = status === "streaming" || status === "submitted" || !!pendingMessageId;

  useEffect(() => {
    if (!chatId) {
      return;
    }
    if (isStreaming) {
      setChatRuntimeStatus(chatId, "streaming");
      return;
    }
    if (status === "error") {
      setChatRuntimeStatus(chatId, "error");
      return;
    }
    setChatRuntimeStatus(chatId, "idle");
  }, [chatId, isStreaming, setChatRuntimeStatus, status]);

  useEffect(() => {
    if (!chatId) {
      return;
    }
    return () => clearChatRuntime(chatId);
  }, [chatId, clearChatRuntime]);

  // Get streaming message parts from the last message if it's streaming
  // Includes both text and reasoning parts from the AI SDK
  const streamingParts = useMemo(() => {
    if (!isStreaming) return undefined;

    const lastMessage = aiMessages[aiMessages.length - 1];
    if (lastMessage?.role === "assistant" && lastMessage.parts) {
      return lastMessage.parts;
    }
    return [];
  }, [aiMessages, isStreaming]);

  const displayMessages = useMemo(() => {
    // Use localHistory for stored messages to reflect branch switches immediately
    const storedMessages = createMessagesList(localHistory);
    let result: ChatMessage[] = [];

    // Map AI messages taking care of pending message
    const pendingUser = pendingUserMessageRef.current;

    // Use AI SDK messages as the source of truth if active, swapping in pendingUser for rich content
    const mappedAiMessages = aiMessages.map((m) => {
      if (pendingUser && m.id === pendingUser.id) {
        return pendingUser;
      }
      return toChatMessage(
        m,
        m.role === "assistant" ? selectedModelId || undefined : undefined,
      );
    });

    if (mappedAiMessages.length > storedMessages.length) {
      result = mappedAiMessages;
    } else {
      result = [...storedMessages];
    }

    if (pendingUser && !result.some((m) => m.id === pendingUser.id)) {
      result.push(pendingUser);
    }

    // Return same reference if content unchanged to prevent re-renders
    if (areMessagesEqual(prevDisplayMessagesRef.current, result)) {
      return prevDisplayMessagesRef.current;
    }
    prevDisplayMessagesRef.current = result;
    return result;
  }, [localHistory, aiMessages, selectedModelId, pendingMessageId]);

  // Keep displayMessagesRef in sync for stable callbacks
  displayMessagesRef.current = displayMessages;

  // Handle sending a message with optional attachments and search
  const handleSendMessage = useCallback(
    async (content: string, options?: MessageInputOptions) => {
      // Allow sending if we are in "new chat" mode (no chatId)
      if (!isUnlocked || (!chat && chatId) || !selectedModelId) {
        if (!selectedModelId) {
          toast.error("Please select a model first");
        }
        return;
      }

      if (!isReady && chatId) {
        if (isLoadingCredentials) {
          toast.error("Loading credentials...");
        } else {
          toast.error("Please add a connection first");
        }
        return;
      }

      // Track message sent
      analytics.chat.messageSent({ model_id: selectedModelId || '' });

      // Clear follow-ups and search sources when sending a new message
      setFollowUps([]);
      isInternalStopRef.current = false;
      if (!options?.searchEnabled) {
        setSearchSources([]);
      }

      const pendingUserMessageId = uuidv4();

      let messageContent: string | MessageContent[] = content;
      const uiParts: Array<{ type: string; text?: string; image?: string }> = [];
      const sendParts: Array<{
        type: "text" | "file";
        text?: string;
        url?: string;
        mediaType?: string;
      }> = [];

      if (options?.attachments && options.attachments.length > 0) {
        const contentParts: MessageContent[] = [];

        for (const attachment of options.attachments) {
          if (attachment.type === "image") {
            const dataUrl = `data:${attachment.mimeType};base64,${attachment.data}`;
            contentParts.push({
              type: "image_url",
              image_url: { url: dataUrl },
            });
            uiParts.push({ type: "image", image: dataUrl });
            sendParts.push({
              type: "file",
              url: dataUrl,
              mediaType: attachment.mimeType,
            });
          } else if (
            attachment.type === "document" ||
            attachment.type === "text"
          ) {
            const dataUrl = `data:${attachment.mimeType};base64,${attachment.data}`;
            contentParts.push({
              type: "document_url",
              document_url: {
                url: dataUrl,
                fileName: attachment.fileName,
                mimeType: attachment.mimeType,
                extractedText: attachment.metadata?.extractedText,
              },
            });

            if (attachment.metadata?.extractedText) {
              const docContext = `[Document: ${attachment.fileName}]\n${attachment.metadata.extractedText}\n\n`;
              sendParts.push({ type: "text", text: docContext });
            }
          }
        }

        if (content) {
          contentParts.push({ type: "text", text: content });
          uiParts.push({ type: "text", text: content });
          sendParts.push({ type: "text", text: content });
        }

        messageContent = contentParts;
      }

      // Show immediately on the current UI if possible
      const userMessage: ChatMessage = {
        id: pendingUserMessageId,
        role: "user",
        content: messageContent,
        created_at: Date.now(),
      };

      pendingUserMessageRef.current = userMessage;
      setPendingMessageId(userMessage.id);
      await new Promise((resolve) => setTimeout(resolve, 10));

      let searchContext = "";

      try {
        // Execute search if enabled
        if (options?.searchEnabled) {
          try {
            const searchResult = await executeSearch(
              content,
              options.searchProvider,
            );
            if (searchResult.results.length > 0) {
              searchContext = formatSearchResultsForContext(
                searchResult.results,
                content,
              );
              toast.success(
                `Found ${searchResult.results.length} search results`,
              );

              // Convert search results to sources for display
              const sources: Source[] = searchResult.results.map(
                (r: SearchResult) => ({
                  sourceType: "url",
                  url: r.url,
                  title: r.title,
                  domain: r.url
                    ? new URL(r.url).hostname.replace(/^www\./, "")
                    : undefined,
                }),
              );
              setSearchSources(sources);
            } else {
              setSearchSources([]);
            }
          } catch (searchError) {
            console.warn("Search failed:", searchError);
            setSearchSources([]);
            // Don't block the message, just warn
            toast.warning(
              "Search failed, sending message without search results",
            );
          }
        }

        // Build the final message text for the AI
        let finalContent = "";
        if (searchContext) {
          finalContent = `${searchContext}\n\n`;
        }

        // Handle multimodal content
        if (Array.isArray(messageContent)) {
          // Extract text parts and prepend search context
          const textParts = messageContent.filter((p) => p.type === "text");

          if (searchContext && textParts.length > 0) {
            textParts[0] = {
              type: "text",
              text: searchContext + "\n\n" + (textParts[0].text || ""),
            };
          } else if (searchContext) {
            messageContent = [
              { type: "text", text: searchContext },
              ...messageContent,
            ];
          }

          // Build UI parts for AI SDK
          for (const part of messageContent) {
            if (part.type === "text" && part.text) {
              uiParts.push({ type: "text", text: part.text });
            } else if (part.type === "image_url" && part.image_url?.url) {
              uiParts.push({ type: "image", image: part.image_url.url });
            }
          }

          finalContent = messageContent
            .filter((p) => p.type === "text")
            .map((p) => p.text)
            .join("\n");
        } else {
          finalContent += messageContent;
          uiParts.push({ type: "text", text: finalContent });
        }

        // Update the pending user message with the fully processed content (including context)
        const fullyProcessedMessage = {
          ...userMessage,
          content: messageContent,
        };
        pendingUserMessageRef.current = fullyProcessedMessage;

        let currentChatId = chatId;

        // Lazy creation: Create chat if it doesn't exist yet
        if (!currentChatId) {
          try {
            setIsGeneratingFollowUps(true);

            const initialTitle = "New Chat";

            // Initialize the history with the fully processed user message so the new 'mounting' ChatPage sees it!
            const history: ChatHistory = {
              currentId: fullyProcessedMessage.id,
              messages: {
                [fullyProcessedMessage.id]: {
                  ...fullyProcessedMessage,
                  childrenIds: []
                }
              },
            };

            const { chatId: newChatId, data: encryptedData } = await createEncryptedChat(
              initialTitle,
              history as unknown as Record<string, unknown>,
            );

            createChatMutation.mutate({
              id: newChatId,
              encryptedChatKey: encryptedData.encryptedChatKey,
              chatKeyNonce: encryptedData.chatKeyNonce,
              encryptedTitle: encryptedData.encryptedTitle,
              titleNonce: encryptedData.titleNonce,
              encryptedChat: encryptedData.encryptedChat,
              chatNonce: encryptedData.chatNonce,
            });

            const optimisticChat = {
              id: newChatId,
              userId: "temp",
              isEncrypted: true,
              encryptedChatKey: encryptedData.encryptedChatKey,
              chatKeyNonce: encryptedData.chatKeyNonce,
              encryptedTitle: encryptedData.encryptedTitle,
              titleNonce: encryptedData.titleNonce,
              encryptedChat: encryptedData.encryptedChat,
              chatNonce: encryptedData.chatNonce,
              folderId: null,
              pinned: false,
              archived: false,
              createdAt: Date.now(),
              updatedAt: Date.now(),
            };

            // Pre-populate the cache with the populated history
            utils.chats.get.setData({ chatId: newChatId }, optimisticChat as any);

            const currentList = utils.chats.list.getData();
            if (currentList) {
              utils.chats.list.setData(undefined, [optimisticChat as any, ...currentList]);
            }

            // Update refs for the global track so handleFinish knows about it
            lastChatIdRef.current = newChatId;
            encryptionKeysRef.current = {
              encryptedChatKey: encryptedData.encryptedChatKey,
              chatKeyNonce: encryptedData.chatKeyNonce,
            };

            // Navigate immediately to the newly created chat ID.
            // We use replace: true to update the current "new chat" state to the real chat URL.
            // We set pending: true so the new mount triggers the AI stream.
            navigate({
              to: "/app/c/$chatId",
              params: { chatId: newChatId },
              search: { pending: true },
              replace: true,
            });

            // Stop execution in this component instance to allow the new mount to handle the stream.
            return;

          } catch (err) {
            console.error("Failed to create chat:", err);
            toast.error("Failed to create new chat");
            setIsGeneratingFollowUps(false);
            return;
          }
        }

        // Send to AI via the hook - use multimodal parts if we have attachments
        if (sendParts.length > 0) {
          // Prepend search context to first text part if present
          if (searchContext && sendParts.length > 0) {
            const firstTextIndex = sendParts.findIndex(
              (p) => p.type === "text",
            );
            if (firstTextIndex >= 0) {
              sendParts[firstTextIndex].text =
                searchContext + "\n\n" + (sendParts[firstTextIndex].text || "");
            } else {
              sendParts.unshift({ type: "text", text: searchContext });
            }
          }
          await sendMessage({ parts: sendParts }, { id: fullyProcessedMessage.id });
        } else {
          await sendMessage(finalContent, { id: fullyProcessedMessage.id });
        }
      } catch (err) {
        pendingUserMessageRef.current = null; // Clear on error
        if ((err as Error).name !== "AbortError") {
          toast.error(
            err instanceof Error ? err.message : "Failed to send message",
          );
        }
      }
    },
    [
      chat,
      chatId,
      isLoadingCredentials,
      isReady,
      isUnlocked,
      selectedModelId,
      sendMessage,
      createChatMutation,
      utils,
    ],
  );

  // Handle message edit - creates a new branch (uses refs for stable callback)
  const handleEditMessage = useCallback(
    async (messageId: string, newContent: string) => {
      const { encryptedChatKey, chatKeyNonce } = encryptionKeysRef.current;
      if (!chatId || !encryptedChatKey || !chatKeyNonce) return;

      const prevHistory = currentHistoryRef.current;

      try {
        // Create a new branch from the edit
        const { history: newHistory } = createBranchFromEdit(
          prevHistory,
          messageId,
          newContent,
        );

        // Update history ref and local state for immediate UI updates
        currentHistoryRef.current = newHistory;
        setLocalHistory(newHistory);

        // Now re-send to get a new AI response (Optimistic Update)
        // The AI messages need to be updated to reflect the new branch
        const newMessages = createMessagesList(newHistory);
        const uiMessages = newMessages.map(toUIMessage);
        setMessages(uiMessages);

        // Trigger a new AI response by sending the edited message securely
        if (selectedModelId && isReady) {
          // Find the user message we just created and get its ID
          const editedUserMessage = newMessages[newMessages.length - 1]; // This is usually the last message in an edit-branch re-run
          // We don't await this immediately so that UI updates without blocking on server mutation
          sendMessage(newContent, { id: editedUserMessage?.id }).catch(console.error);
        }

        // Encrypt and save to server
        const encrypted = encryptChatContent(
          chatId,
          encryptedChatKey,
          chatKeyNonce,
          newHistory as unknown as Record<string, unknown>,
        );

        await updateChatMutationRef.current.mutateAsync({
          id: chatId,
          data: {
            encryptedChat: encrypted.encryptedChat,
            chatNonce: encrypted.chatNonce,
          },
        });

      } catch (error) {
        // Rollback state
        currentHistoryRef.current = prevHistory;
        setLocalHistory(prevHistory);
        const prevMessages = createMessagesList(prevHistory);
        setMessages(prevMessages.map(toUIMessage));

        console.error("Failed to edit message:", error);
        toast.error("Failed to edit message");
      }
    },
    [chatId, setMessages, selectedModelId, isReady, sendMessage],
  );

  // Handle branch switching (uses refs for stable callback)
  const handleSwitchBranch = useCallback(
    async (messageId: string) => {
      const { encryptedChatKey, chatKeyNonce } = encryptionKeysRef.current;
      if (!chatId || !encryptedChatKey || !chatKeyNonce) return;

      const prevHistory = currentHistoryRef.current;

      try {
        // Switch to the new branch
        const newHistory = switchToBranch(prevHistory, messageId);

        // Update history ref and local state immediately for responsive UI
        currentHistoryRef.current = newHistory;
        setLocalHistory(newHistory);

        // Update AI messages to reflect the switched branch OPTIMISTICALLY
        const newMessages = createMessagesList(newHistory);
        const uiMessages = newMessages.map(toUIMessage);
        setMessages(uiMessages);

        // Load follow-ups from the new branch's last assistant message
        const lastMessage = newMessages[newMessages.length - 1];
        if (
          lastMessage?.role === "assistant" &&
          lastMessage.followUps &&
          lastMessage.followUps.length > 0
        ) {
          setFollowUps(lastMessage.followUps);
        } else {
          setFollowUps([]);
        }

        // Encrypt and save to server (async, don't block UI)
        const encrypted = encryptChatContent(
          chatId,
          encryptedChatKey,
          chatKeyNonce,
          newHistory as unknown as Record<string, unknown>,
        );

        await updateChatMutationRef.current.mutateAsync({
          id: chatId,
          data: {
            encryptedChat: encrypted.encryptedChat,
            chatNonce: encrypted.chatNonce,
          },
        });
      } catch (error) {
        // Rollback state
        currentHistoryRef.current = prevHistory;
        setLocalHistory(prevHistory);
        const prevMessages = createMessagesList(prevHistory);
        setMessages(prevMessages.map(toUIMessage));

        console.error("Failed to switch branch:", error);
        toast.error("Failed to switch branch");
      }
    },
    [chatId, setMessages],
  );

  // Handle message regeneration (uses refs for stable callback during streaming)
  const handleRegenerateMessage = useCallback(
    async (messageId: string, options?: { modifier?: string }) => {
      // Check if we have encryption keys (means chat is loaded)
      const { encryptedChatKey } = encryptionKeysRef.current;
      if (!encryptedChatKey || !chatId || !selectedModelId || !isReady) return;

      analytics.chat.messageRegenerated({ model_id: selectedModelId || '' });

      // Use ref to get current messages without recreating callback during streaming
      const currentDisplayMessages = displayMessagesRef.current;

      try {
        // Find the message index
        const messageIndex = currentDisplayMessages.findIndex(
          (m) => m.id === messageId,
        );
        if (messageIndex === -1) return;

        // Get all messages up to (but not including) the message to regenerate
        // This should include the user message that prompted this response
        const messagesToKeep = currentDisplayMessages.slice(0, messageIndex);
        const userMessage = messagesToKeep[messagesToKeep.length - 1];

        if (!userMessage || userMessage.role !== "user") {
          toast.error("Cannot regenerate: no user message found");
          return;
        }

        // Clear follow-ups
        setFollowUps([]);
        isInternalStopRef.current = false;

        // Get the user message content
        let userContent =
          typeof userMessage.content === "string"
            ? userMessage.content
            : userMessage.content
              .filter((c) => c.type === "text")
              .map((c) => c.text)
              .join("\n");

        // Apply modifier if provided (e.g., "Please be more concise")
        if (options?.modifier) {
          userContent = `${userContent}\n\n${options.modifier}`;
        }

        // Rewind history pointer to the parent of the user message
        // This is CRITICAL to avoid creating cycles in the message tree if we are reusing the user message ID
        if (userMessage.parentId) {
          currentHistoryRef.current.currentId = userMessage.parentId;
        } else {
          // If no parent, we are at the root
          currentHistoryRef.current.currentId = null;
        }

        // Set up messages for regeneration (exclude the user message as sendMessage will add it back)
        const uiMessages = messagesToKeep.slice(0, -1).map(toUIMessage);
        setMessages(uiMessages);

        // Store the pending user message for persistence (without modifier in stored content)
        pendingUserMessageRef.current = {
          ...userMessage,
          id: userMessage.id, // Keep same ID for the original user message
        };

        // Send the message again to regenerate
        await sendMessage(userContent, { id: userMessage.id });
      } catch (error) {
        console.error("Failed to regenerate message:", error);
        toast.error("Failed to regenerate message");
      }
    },
    [chatId, selectedModelId, isReady, setMessages, sendMessage],
  );

  // Handle message deletion - uses refs to avoid callback recreation during streaming
  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      const { encryptedChatKey, chatKeyNonce } = encryptionKeysRef.current;
      if (!chatId || !encryptedChatKey || !chatKeyNonce) return;

      const prevHistory = currentHistoryRef.current;

      try {
        // Delete the message and its children from history
        const newHistory = deleteMessage(
          prevHistory,
          messageId,
          true,
        );

        // Update history ref and local state for immediate UI updates
        currentHistoryRef.current = newHistory;
        setLocalHistory(newHistory);

        // Update AI messages to reflect the deletion OPTIMISTICALLY
        const newMessages = createMessagesList(newHistory);
        const uiMessages = newMessages.map(toUIMessage);
        setMessages(uiMessages);

        // Clear follow-ups if we deleted the last message
        if (
          newMessages.length === 0 ||
          newMessages[newMessages.length - 1]?.role !== "assistant"
        ) {
          setFollowUps([]);
        }

        // Encrypt and save to server
        const encrypted = encryptChatContent(
          chatId,
          encryptedChatKey,
          chatKeyNonce,
          newHistory as unknown as Record<string, unknown>,
        );

        await updateChatMutationRef.current.mutateAsync({
          id: chatId,
          data: {
            encryptedChat: encrypted.encryptedChat,
            chatNonce: encrypted.chatNonce,
          },
        });

      } catch (error) {
        // Rollback state
        currentHistoryRef.current = prevHistory;
        setLocalHistory(prevHistory);
        const prevMessages = createMessagesList(prevHistory);
        setMessages(prevMessages.map(toUIMessage));

        console.error("Failed to delete message:", error);
        toast.error("Failed to delete message");
      }
    },
    [chatId, setMessages],
  );

  // Memoize navbar children to prevent ChatNavbar re-renders
  // Must be before early returns to maintain consistent hook order
  const navbarChildren = useMemo(
    () => (
      <ModelSelector
        value={selectedModelId || ""}
        onChange={setSelectedModel}
      />
    ),
    [selectedModelId, setSelectedModel],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-white dark:bg-gray-900">
        <Spinner size="lg" className="text-gray-500 dark:text-gray-400" />
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="flex items-center justify-center h-full bg-white dark:bg-gray-900">
        <div className="text-center max-w-md px-4">
          <Alert variant="destructive" className="bg-gray-50 dark:bg-gray-850 border-gray-100 dark:border-gray-850">
            <HugeiconsIcon icon={Alert01Icon} className="h-4 w-4" />
            <AlertTitle className="text-gray-900 dark:text-gray-100">Chat not found</AlertTitle>
            <AlertDescription className="text-gray-500 dark:text-gray-400">
              This conversation may have been deleted or doesn't exist.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full w-full min-w-0 overflow-hidden">
      {/* Chat header - Absolute overlay */}
      <ChatNavbar chatId={chatId || ""}>{navbarChildren}</ChatNavbar>

      {chat.decryptionError && (
        <div className="px-4 pt-16 sm:px-6">
          <Alert className="bg-amber-500/10 border-amber-500/30">
            <HugeiconsIcon icon={Alert01Icon} className="h-4 w-4 text-amber-500" />
            <AlertTitle className="text-gray-900 dark:text-gray-100">
              Couldn&apos;t decrypt conversation
            </AlertTitle>
            <AlertDescription className="text-gray-500 dark:text-gray-400 flex items-center justify-between gap-3">
              <span>{chat.decryptionError}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDecryptAttempt((prev) => prev + 1)}
              >
                Retry Decrypt
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Messages - Takes full space */}
      <div className="relative flex-1 w-full h-full min-w-0 overflow-hidden">
        <Messages
          messages={displayMessages}
          history={localHistory}
          streamingParts={streamingParts}
          streamingSources={searchSources}
          isStreaming={isStreaming}
          currentModelId={selectedModelId || undefined}
          onEditMessage={handleEditMessage}
          onRegenerateMessage={handleRegenerateMessage}
          onDeleteMessage={handleDeleteMessage}
          onSwitchBranch={handleSwitchBranch}
          onSendMessage={handleSendMessage}
          inputDisabled={!isUnlocked || !selectedModelId}
          followUps={followUps}
          isGeneratingFollowUps={isGeneratingFollowUps}
          onFollowUpSelect={handleSendMessage}
          highlightMessageId={highlightMessageId}
        />
      </div>

      {/* Input area - Fixed at bottom with iOS-style glassmorphism */}
      <footer className={cn(
        "z-20 w-full transition-all duration-300",
        // Mobile: Fixed bottom bar with glass effect
        "fixed bottom-0 left-0 right-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-t border-gray-200/50 dark:border-gray-800/50 px-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] pt-3",
        // Desktop: Standard absolute positioning (existing style)
        "sm:absolute sm:bottom-0 sm:left-0 sm:right-0 sm:bg-transparent sm:backdrop-blur-none sm:border-0 sm:px-6 sm:pb-6 sm:pt-0 sm:pointer-events-none"
      )}>
        <div className="max-w-4xl mx-auto w-full pointer-events-auto">
          <MessageInput
            onSend={handleSendMessage}
            onStop={stop}
            isStreaming={isStreaming}
            disabled={!isUnlocked || !selectedModelId}
            placeholder={
              !isUnlocked
                ? "Unlock E2EE to send messages"
                : !selectedModelId
                  ? "Select a model to continue"
                  : "Message Onera..."
            }
          />
        </div>
      </footer>
    </div>
  );
}
